import { useImperativeHandle, useMemo, useState } from 'react';

import { FlexAligner, Label, Switch } from '@scaleaq/scaleui';
import { DateTime } from 'luxon';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SegmentedChips } from '@/components/chips';
import { ControlledDateField } from '@/components/inputFields/ControlledDateField';
import { ControlledRadioField } from '@/components/inputFields/ControlledRadioField';
import { ControlledTextOrNumberField } from '@/components/inputFields/ControlledTextOrNumberField';
import { responsive } from '@/utils';

import { useLiveChartOptions } from '../../hooks.ts';
import {
  ChartConfigProps,
  ChartInterval,
  ChartType,
  LinearChartConfigType,
  LineDrawStyle,
  PlotType,
} from '../../types.ts';
import { useDrawStyleChips } from '../../useDrawStyleChips.ts';
import { MultiSelectSensorAndTypeFields } from './MultiSelectSensorAndTypeFields.tsx';

export const LinearChartConfig = ({
  onSave,
  config,
  id,
  ref,
  rootId,
}: ChartConfigProps<LinearChartConfigType>) => {
  const { t } = useTranslation(['chartConfig']);

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<LinearChartConfigType>({
    defaultValues: {
      interval: ChartInterval.Day,
      drawStyle: config?.pointsOnly ? LineDrawStyle.POINTS : LineDrawStyle.LINE,
      ...config,
      plotType: PlotType.LINEAR,
    },
  });

  const selectedType = useWatch({ control, name: 'type' });
  const selectedDrawStyle = useWatch({ control, name: 'drawStyle' });
  const selectedFromDate = useWatch({ control, name: 'from' });
  const selectedToDate = useWatch({ control, name: 'to' });

  const [hasReference, setHasReference] = useState<boolean>(() => !!config?.reference);

  const { chartIntervalOptions: intervalOptions } = useLiveChartOptions();

  const triggerSave = async () => {
    if (!selectedFromDate) setValue('from', DateTime.now());
    if (!selectedToDate) setValue('to', DateTime.now());
    if (!hasReference) setValue('reference', undefined);
    const isValid = await trigger();
    if (isValid) onSave(id, getValues());
  };

  useImperativeHandle(ref, () => ({
    save: () => triggerSave().catch(err => console.error(err)),
  }));

  const chartTypeOptions = useMemo(
    () =>
      Object.entries(ChartType).map(([key, value]) => ({
        value,
        label: t(`chartConfig:chartType.${key as keyof typeof ChartType}`),
      })),
    [t]
  );

  const drawStyleChips = useDrawStyleChips(selectedDrawStyle);

  return (
    <FlexAligner direction={'column'} gap={responsive.spacing['5xlarge']}>
      <ControlledTextOrNumberField
        control={control}
        name={'title'}
        label={t('chartConfig:chartConfigModal.fields.title')}
      />
      <MultiSelectSensorAndTypeFields
        control={control}
        errors={errors}
        rootId={rootId}
        sensorRequiredErrorKey={t('chartConfig:linear.isRequired.sensor')}
      />
      <ControlledRadioField
        control={control}
        label={t('chartConfig:chartConfigModal.fields.chartType')}
        name={'type'}
        options={chartTypeOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.chartType'),
        }}
        caution={errors.type?.message}
      />
      <ControlledRadioField
        control={control}
        name={'interval'}
        label={t('chartConfig:chartConfigModal.fields.interval')}
        options={intervalOptions}
        render={selectedType === ChartType.LIVE}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.interval'),
        }}
        caution={errors.interval?.message}
      />
      <ControlledDateField
        control={control}
        name={'from'}
        label={t('chartConfig:linear.labels.from')}
        render={selectedType === ChartType.HISTORICAL}
        rules={{
          required: t('chartConfig:linear.isRequired.from'),
        }}
        caution={errors.from?.message}
      />
      <ControlledDateField
        control={control}
        name={'to'}
        label={t('chartConfig:linear.labels.to')}
        render={selectedType === ChartType.HISTORICAL}
        rules={{
          required: t('chartConfig:linear.isRequired.to'),
        }}
        caution={errors.to?.message}
      />
      <Label label={t('chartConfig:linear.labels.drawStyle')}>
        <SegmentedChips
          size='small'
          items={drawStyleChips.items}
          value={drawStyleChips.selected}
          onChange={item => item.value && setValue('drawStyle', item.value)}
        />
      </Label>
      <FlexAligner direction={'column'} gap={responsive.spacing['xsmall']}>
        <Label label={t('chartConfig:linear.labels.reference')} />
        <FlexAligner direction={'row'} alignment={'center-left'} gap={responsive.spacing.xlarge}>
          <Switch checked={hasReference} onChange={() => setHasReference(prev => !prev)} />
          <ControlledTextOrNumberField
            control={control}
            name={'reference.label'}
            disabled={!hasReference}
            placeholder={t('chartConfig:linear.labels.label')}
          />
          <ControlledTextOrNumberField
            control={control}
            inputType={'number'}
            name={'reference.value'}
            maxWidth={'5rem'}
            disabled={!hasReference}
            placeholder={t('chartConfig:linear.labels.value')}
            rules={{
              required: hasReference ? t('chartConfig:linear.isRequired.reference') : false,
            }}
            caution={errors.reference?.value?.message}
          />
        </FlexAligner>
      </FlexAligner>
    </FlexAligner>
  );
};
